import React, { useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  FormGroup,
  HelperText,
  HelperTextItem,
  Label,
  LabelGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Radio,
  TextInput,
  Tooltip,
} from '@patternfly/react-core';
import { ExternalLinkAltIcon } from '@patternfly/react-icons';
import { LabelSelect, SegmentedTimeInput } from '../common';
import { useAvailableLabels } from '../../hooks';
import { TimeLog } from '../../types';
import { HAS_API, JIRA_BASE_URL } from '../../config';
import {
  calculateTimeSpent,
  formatDateISO,
  formatTimeInput,
  formatTimeSpent,
  getLabelColor,
  parseDurationString,
  parseTimeInput,
} from '../../utils';
import { useTimeCalculation } from '../../hooks/useTimeCalculation';
import './EditTimeLogModal.css';

type EditMode = 'duration' | 'timeRange';

interface EditTimeLogModalProps {
  isOpen: boolean;
  timeLog: TimeLog | null;
  onClose: () => void;
  onSave: (id: string, startedTime: string, timeSpentSeconds: number, labels?: string[]) => Promise<void>;
}

export const EditTimeLogModal: React.FC<EditTimeLogModalProps> = ({ isOpen, timeLog, onClose, onSave }) => {
  const { labels: availableLabels } = useAvailableLabels();

  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [durationInput, setDurationInput] = useState('');
  const [editMode, setEditMode] = useState<EditMode>('duration');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Labels state
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

  useEffect(() => {
    if (timeLog) {
      const startDate = new Date(timeLog.startedTime);
      setDate(formatDateISO(startDate));
      setStartTime(formatTimeInput(startDate));

      const endDate = new Date(startDate.getTime() + timeLog.timeSpentSeconds * 1000);
      setEndTime(formatTimeInput(endDate));

      setDurationInput(formatTimeSpent(timeLog.timeSpentSeconds));
      setSelectedLabels(timeLog.issueLabels || []);
      setError(null);
    }
  }, [timeLog]);

  const { calculatedEndTime, calculatedDuration } = useTimeCalculation(editMode, date, startTime, endTime, durationInput);

  const handleDateChange = (_event: React.FormEvent<HTMLInputElement>, value: string) => {
    setDate(value);
  };

  const handleModeChange = (mode: EditMode) => {
    setEditMode(mode);
    setError(null);

    // Sync values when switching modes
    if (mode === 'timeRange' && calculatedEndTime) {
      setEndTime(calculatedEndTime);
    } else if (mode === 'duration' && calculatedDuration && !calculatedDuration.error) {
      setDurationInput(formatTimeSpent(calculatedDuration.seconds));
    }
  };

  const handleSave = async () => {
    if (!timeLog) return;

    setError(null);
    setIsSaving(true);

    try {
      const baseDate = new Date(date);
      if (isNaN(baseDate.getTime())) {
        setError('Select a valid date to continue.');
        setIsSaving(false);
        return;
      }

      const newStartDate = parseTimeInput(startTime, '00', baseDate);
      let timeSpentSeconds: number;

      if (editMode === 'duration') {
        const parsed = parseDurationString(durationInput);
        if (parsed === null || parsed <= 0) {
          setError('Enter a valid duration (e.g., "2h 30m", "90m", "1.5h").');
          setIsSaving(false);
          return;
        }
        timeSpentSeconds = parsed;
      } else {
        const newEndDate = parseTimeInput(endTime, '00', baseDate);
        if (newEndDate <= newStartDate) {
          setError('End time must be after start time.');
          setIsSaving(false);
          return;
        }
        timeSpentSeconds = calculateTimeSpent(newStartDate, newEndDate);
      }

      await onSave(timeLog.id, newStartDate.toISOString(), timeSpentSeconds, HAS_API ? selectedLabels : undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save. Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const jiraIssueUrl = timeLog?.issueKey ? `${JIRA_BASE_URL}/browse/${timeLog.issueKey}` : null;

  const parsedDuration = editMode === 'duration' ? parseDurationString(durationInput) : null;
  const isValid = editMode === 'duration'
    ? parsedDuration !== null && parsedDuration > 0
    : calculatedDuration && !calculatedDuration.error;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="edit-timelog-modal-title"
      aria-describedby="edit-timelog-modal-body"
      variant="small"
    >
      <ModalHeader
        title="Edit work log"
        labelId="edit-timelog-modal-title"
      />
      <ModalBody id="edit-timelog-modal-body">
        {/* Compact issue info section */}
        {(timeLog?.issueKey || timeLog?.issueSummary) && (
          <div className="edit-timelog-issue-compact">
            {timeLog?.issueKey && jiraIssueUrl && (
              <a
                href={jiraIssueUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="edit-timelog-issue-link"
              >
                {timeLog.issueKey}
                <ExternalLinkAltIcon className="edit-timelog-issue-link-icon" />
              </a>
            )}
            {timeLog?.issueSummary && (
              <Tooltip content={timeLog.issueSummary} position="bottom">
                <span className="edit-timelog-issue-summary-compact">{timeLog.issueSummary}</span>
              </Tooltip>
            )}
          </div>
        )}

        <Form>
          {/* Labels */}
          {HAS_API ? (
            <FormGroup label="Labels" fieldId="edit-labels">
              <LabelSelect
                availableLabels={availableLabels}
                selectedLabels={selectedLabels}
                onSelectionChange={setSelectedLabels}
                isDisabled={isSaving}
                labelGroupClassName="edit-timelog-selected-labels"
              />
            </FormGroup>
          ) : selectedLabels.length > 0 && (
            <FormGroup fieldId="edit-labels">
              <LabelGroup className="edit-timelog-selected-labels">
                {selectedLabels.map((label) => (
                  <Label key={label} color={getLabelColor(label)}>
                    {label}
                  </Label>
                ))}
              </LabelGroup>
            </FormGroup>
          )}

          {/* Date field */}
          <FormGroup label="Date" isRequired fieldId="edit-date">
            <DatePicker
              id="edit-date"
              value={date}
              onChange={handleDateChange}
              aria-label="Select date"
              appendTo={() => document.body}
            />
          </FormGroup>

          {/* Edit mode selector */}
          <div className="edit-timelog-mode-selector">
            <Radio
              isChecked={editMode === 'duration'}
              name="edit-mode"
              onChange={() => handleModeChange('duration')}
              label="Duration"
              id="edit-mode-duration"
            />
            <Radio
              isChecked={editMode === 'timeRange'}
              name="edit-mode"
              onChange={() => handleModeChange('timeRange')}
              label="Time range"
              id="edit-mode-timerange"
            />
          </div>

          {/* Duration mode */}
          {editMode === 'duration' && (
            <div className="edit-timelog-duration-mode">
              <FormGroup label="Start time" isRequired fieldId="edit-start-time">
                <SegmentedTimeInput
                  id="edit-start-time"
                  value={startTime}
                  onChange={setStartTime}
                  ariaLabel="Start time"
                />
              </FormGroup>

              <FormGroup
                label="Duration"
                isRequired
                fieldId="edit-duration"
              >
                <TextInput
                  type="text"
                  id="edit-duration"
                  value={durationInput}
                  onChange={(_event, value) => setDurationInput(value)}
                  placeholder="e.g., 2h 30m"
                  aria-label="Duration"
                />
                <HelperText>
                  <HelperTextItem variant="default">
                    Format: 2h 30m, 90m, or 1.5h
                  </HelperTextItem>
                </HelperText>
              </FormGroup>

              {calculatedEndTime && (
                <div className="edit-timelog-calculated-info">
                  End time: {calculatedEndTime}
                </div>
              )}
            </div>
          )}

          {/* Time range mode */}
          {editMode === 'timeRange' && (
            <div className="edit-timelog-timerange-mode">
              <div className="edit-timelog-time-row">
                <FormGroup label="Start time" isRequired fieldId="edit-start-time-range">
                  <SegmentedTimeInput
                    id="edit-start-time-range"
                    value={startTime}
                    onChange={setStartTime}
                    ariaLabel="Start time"
                  />
                </FormGroup>

                <FormGroup label="End time" isRequired fieldId="edit-end-time">
                  <SegmentedTimeInput
                    id="edit-end-time"
                    value={endTime}
                    onChange={setEndTime}
                    ariaLabel="End time"
                  />
                </FormGroup>
              </div>

              {calculatedDuration && (
                <div className={`edit-timelog-calculated-info ${calculatedDuration.error ? 'edit-timelog-calculated-info--error' : ''}`}>
                  {calculatedDuration.error
                    ? 'End time must be after start time'
                    : `Duration: ${formatTimeSpent(calculatedDuration.seconds)}`}
                </div>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <HelperText>
              <HelperTextItem variant="error">{error}</HelperTextItem>
            </HelperText>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          key="save"
          variant="primary"
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={isSaving || !isValid}
        >
          Save
        </Button>
        <Button key="cancel" variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
